import { useEffect, useId, useRef, useState } from "react";
import { Linking, Platform, Pressable, StyleSheet, Text, View } from "react-native";
import { resolveRegisterAddress, suggestRegisterAddress, type AddressSuggestion, type RegisterAddress, type RegisterAddressContext } from "../lib/register-address";
import { central } from "../theme/tokens";
import { Field } from "./ui";

/** Suggestions live only in this editor and disappear when the guest leaves it. */
export function RegisterStreetAddress({ context, value, onChange, onSelect, onPendingChange }: {
  context: RegisterAddressContext;
  value: string;
  onChange: (street: string) => void;
  onSelect: (address: RegisterAddress) => void;
  onPendingChange: (pending: boolean) => void;
}) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(-1);
  const [unavailable, setUnavailable] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [sessionToken, setSessionToken] = useState(newSessionToken);
  const revision = useRef(0);
  const selecting = useRef(false);
  const listId = useId();
  useEffect(() => () => { revision.current++; }, []);
  useEffect(() => {
    setSuggestions([]);
    setActive(-1);
    if (!focused || value.trim().length < 4 || resolving) return;
    const current = ++revision.current;
    const timer = setTimeout(() => {
      void suggestRegisterAddress(context, value.trim(), sessionToken).then(results => {
        if (revision.current === current) { setSuggestions(results); setUnavailable(false); }
      }).catch(() => { if (revision.current === current) setUnavailable(true); });
    }, 350);
    return () => clearTimeout(timer);
  }, [value, focused, context.funeralHomeId, context.slug, context.eventId, sessionToken, resolving]);

  const choose = async (suggestion: AddressSuggestion) => {
    // Start on press-in so a slow touch cannot lose its option to input blur.
    if (selecting.current) return;
    selecting.current = true;
    onPendingChange(true);
    setFocused(false);
    setSuggestions([]);
    setResolving(true);
    const current = ++revision.current;
    try {
      const address = await resolveRegisterAddress(context, suggestion.id, sessionToken);
      if (revision.current === current) { onSelect(address); setUnavailable(false); }
    } catch { if (revision.current === current) setUnavailable(true); }
    finally { if (revision.current === current) { selecting.current = false; setResolving(false); setSessionToken(newSessionToken()); onPendingChange(false); } }
  };
  const keyboard = Platform.OS === "web" ? {
    onKeyPress: (event: { nativeEvent: { key: string }; preventDefault: () => void }) => {
      const key = event.nativeEvent.key;
      if (key === "Escape") { setSuggestions([]); setFocused(false); }
      if (suggestions.length && (key === "ArrowDown" || key === "ArrowUp")) {
        event.preventDefault();
        setActive(index => key === "ArrowDown" ? (index + 1) % suggestions.length : (index <= 0 ? suggestions.length : index) - 1);
      }
      if (key === "Enter" && active >= 0 && suggestions[active]) { event.preventDefault(); void choose(suggestions[active]); }
    },
    "aria-autocomplete": "list" as const,
    "aria-expanded": focused && suggestions.length > 0,
    "aria-controls": suggestions.length ? listId : undefined,
    "aria-activedescendant": active >= 0 ? `${listId}-${active}` : undefined,
  } : {};
  return <View style={styles.container}>
    <Field {...keyboard} accessibilityRole="combobox" autoComplete="off" autoCorrect={false} spellCheck={false} textContentType="none" label="Street Address" required autoFocus value={value}
      editable={!resolving} onChangeText={street => { revision.current++; setFocused(true); onChange(street); }}
      onFocus={() => { if (!selecting.current) setFocused(true); }}
      onBlur={() => { if (!selecting.current) { setSuggestions([]); setFocused(false); } }}
      placeholder={resolving ? "Filling address…" : unavailable ? "Enter street address manually" : "Start typing your street address"}
      autoCapitalize="words" maxLength={180} inputStyle={styles.input} />
    {focused && suggestions.length > 0 ? <View nativeID={listId} {...(Platform.OS === "web" ? ({ role: "listbox" } as never) : { accessibilityRole: "list" as const })} accessibilityLabel="Address Suggestions" style={styles.suggestions}>
      {suggestions.map((suggestion, index) => <Pressable key={suggestion.id} nativeID={`${listId}-${index}`} role="option" accessibilityState={{ selected: active === index }} {...(Platform.OS === "web" ? { onPointerDown: (event: { button?: number; nativeEvent?: { button?: number }; preventDefault: () => void }) => {
        if ((event.button ?? event.nativeEvent?.button ?? 0) !== 0) return;
        event.preventDefault();
        void choose(suggestion);
      } } : { onPressIn: () => void choose(suggestion) })} onPress={() => void choose(suggestion)} style={({ pressed }) => [styles.option, (pressed || active === index) && styles.active]}>
        <Text numberOfLines={2} style={styles.optionText}>{suggestion.label}</Text>
      </Pressable>)}
      <View style={styles.attribution}><Text style={styles.credit}>Google Maps</Text><Text accessibilityRole="link" onPress={() => void Linking.openURL("https://policies.google.com/privacy")} style={styles.privacy}>Privacy</Text></View>
    </View> : null}
  </View>;
}
function newSessionToken() {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `addr${Date.now()}${Math.random().toString(36).slice(2)}`.padEnd(32, "0").slice(0, 36);
}

const styles = StyleSheet.create({
  container: { position: "relative", zIndex: 20 },
  input: { minHeight: 44, height: 44, paddingVertical: 8 },
  suggestions: { position: "absolute", top: "100%", left: 0, right: 0, backgroundColor: central.surface.raised, borderWidth: 1, borderColor: central.border.strong, borderRadius: 12, overflow: "hidden", zIndex: 30 },
  option: { minHeight: 44, paddingHorizontal: 12, paddingVertical: 10, justifyContent: "center", borderBottomWidth: 1, borderBottomColor: central.border.subtle },
  active: { backgroundColor: central.surface.canvas },
  optionText: { color: central.text.primary, fontFamily: central.typography.body, fontSize: 13, lineHeight: 18 },
  attribution: { minHeight: 28, flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingHorizontal: 12, paddingVertical: 6 },
  credit: { color: central.text.secondary, fontFamily: "Arial", fontSize: 14 },
  privacy: { color: central.action.primary, fontFamily: central.typography.body, fontSize: 12, textDecorationLine: "underline" },
});
